/**
 * /admin/catalogos-r2 — gestão dos 4 catálogos novos do R-IMPORT 1.
 *
 * NÃO substitui a `CatalogsPage` legacy (que gerencia produtos/serviços/origens
 * do esquema R0 via tabela `catalogs`). São sistemas paralelos com schemas
 * próprios — R-IMPORT 1 catálogos têm campos específicos (cor, icone, flags
 * semânticos, etc).
 *
 * Sub-abas (uma por catálogo):
 *   - Categorias de Serviço (service_categories)
 *   - Papéis de Vínculo (relationship_roles)
 *   - Segmentos de Cliente (client_segments)
 *   - Templates de Contrato (contract_templates)
 *
 * Cada sub-aba tem lista + form inline pra create/edit/soft-delete.
 *
 * Sprint R-IMPORT 2 (2026-05-26).
 */

import { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { ComercialIcon } from '@/components/icons';
import {
  useServiceCategories,
  useServiceCategoryActions,
  useRelationshipRoles,
  useRelationshipRoleActions,
  useContractTemplates,
  useContractTemplateActions,
  type ServiceCategory,
  type RelationshipRole,
  type ContractTemplate,
} from '@/lib/r-import-catalogs-store';

// P3 (AJUSTES-POS-2): a sub-aba "Segmentos de Cliente" (gestão manual de
// client_segments) foi removida. Segmento agora vem das TAGS do Omie e é só
// leitura no v2 — ninguém cria/edita segmento por aqui. O catálogo legado
// (client_segments) segue no banco pra round-trip, só não tem mais tela.
type TabSlug =
  | 'service-categories'
  | 'relationship-roles'
  | 'contract-templates';

const TABS: Array<{ slug: TabSlug; label: string; desc: string }> = [
  {
    slug: 'service-categories',
    label: 'Categorias de Serviço',
    desc: 'Agrupa os serviços por tipo (manutenção, monitoramento, instalação…).',
  },
  {
    slug: 'relationship-roles',
    label: 'Papéis de Vínculo',
    desc: 'Diz como cada pessoa se liga ao cliente: síndico, administradora, gerente predial…',
  },
  {
    slug: 'contract-templates',
    label: 'Templates de Contrato',
    desc: 'Modelos prontos que preenchem o passo a passo de fechar contrato (Manutenção Padrão, Premium…).',
  },
];

/**
 * FAXINA 2026-07-31: a prop `embedded` saiu. O único uso em app é dentro de
 * `CatalogsPage` (área "Avançados"), que já provê o header — o ramo `!embedded`
 * (PageHeader + Alert próprios) era inalcançável desde que a rota direta
 * `/admin/catalogos-r2` virou redirect (`App.tsx`).
 */
export function CatalogsR2Page() {
  const [tab, setTab] = useState<TabSlug>('service-categories');
  const current = TABS.find((t) => t.slug === tab)!;

  return (
    <section data-testid="catalogs-r2-page">
      {/* FASE-C: pílulas com `flex-wrap` (quebravam em 2 linhas no 375px)
          viraram o Tabs canônico — 1 linha rolável, mesmos rótulos/ordem/testids. */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabSlug)} className="mb-4">
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger
              key={t.slug}
              value={t.slug}
              data-testid={`catalogs-r2-tab-${t.slug}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-4">
        {current.desc}
      </p>

      {tab === 'service-categories' && <ServiceCategoriesPanel />}
      {tab === 'relationship-roles' && <RelationshipRolesPanel />}
      {tab === 'contract-templates' && <ContractTemplatesPanel />}
    </section>
  );
}

// ─── ServiceCategoriesPanel ────────────────────────────────────────────

function ServiceCategoriesPanel() {
  const { data, isLoading } = useServiceCategories({ includeInactive: true });
  const { create, update, remove } = useServiceCategoryActions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceCategory | null>(null);

  const rows = useMemo(() => data ?? [], [data]);

  function handleSave(input: {
    slug: string;
    nome: string;
    descricao: string;
    cor: string;
    ordem: number;
  }) {
    const payload = {
      slug: input.slug.trim(),
      nome: input.nome.trim(),
      descricao: input.descricao.trim() || null,
      cor: input.cor.trim() || null,
      ordem: input.ordem,
    };
    if (editing) {
      update.mutate(
        { id: editing.id, input: payload },
        { onSuccess: () => closeDialog() },
      );
    } else {
      create.mutate(payload, { onSuccess: () => closeDialog() });
    }
  }

  function closeDialog() {
    setOpen(false);
    setEditing(null);
  }

  return (
    <Card variant="flat">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-line dark:border-line-dark">
        <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
          {rows.length} {rows.length === 1 ? 'categoria' : 'categorias'}
        </p>
        <Button
          size="sm"
          data-testid="cr2-new-service-category"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          + Nova categoria
        </Button>
      </div>
      {isLoading ? (
        <p className="p-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ComercialIcon className="w-6 h-6" />}
          title="Nenhuma categoria"
          description="Adicione a primeira pra começar."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Identificador</TableHead>
              <TableHead>Cor</TableHead>
              <TableHead className="text-center">Ordem</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`cr2-cat-row-${r.id}`}>
                <TableCell>{r.nome}</TableCell>
                <TableCell>
                  <code className="break-all text-xs text-ink-3 dark:text-ink-dark-3">
                    {r.slug}
                  </code>
                </TableCell>
                <TableCell>
                  {r.cor ? (
                    <span
                      className="inline-block w-4 h-4 rounded-full border border-line dark:border-line-dark"
                      style={{ backgroundColor: r.cor }}
                      title={r.cor}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-center font-mono">{r.ordem}</TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={r.ativo}
                    onChange={() =>
                      update.mutate({ id: r.id, input: { ativo: !r.ativo } })
                    }
                    aria-label={`Ligar/desligar ${r.nome}`}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(r);
                      setOpen(true);
                    }}
                  >
                    Editar
                  </Button>
                  {r.ativo && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(r.id)}
                      className="text-red"
                    >
                      Desativar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ServiceCategoryDialog
        open={open}
        onClose={closeDialog}
        editing={editing}
        onSubmit={handleSave}
        busy={create.isPending || update.isPending}
      />
    </Card>
  );
}

function ServiceCategoryDialog({
  open,
  onClose,
  editing,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  editing: ServiceCategory | null;
  onSubmit: (input: {
    slug: string;
    nome: string;
    descricao: string;
    cor: string;
    ordem: number;
  }) => void;
  busy?: boolean;
}) {
  const [slug, setSlug] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [cor, setCor] = useState('');
  const [ordem, setOrdem] = useState(0);

  // Reset on open
  useMemo(() => {
    if (open) {
      setSlug(editing?.slug ?? '');
      setNome(editing?.nome ?? '');
      setDescricao(editing?.descricao ?? '');
      setCor(editing?.cor ?? '');
      setOrdem(editing?.ordem ?? 0);
    }
  }, [open, editing]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>
          {editing ? `Editar: ${editing.nome}` : 'Nova categoria de serviço'}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cr2-cat-nome" required>
              Nome
            </Label>
            <Input
              id="cr2-cat-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Manutenção"
            />
          </div>
          <div>
            <Label htmlFor="cr2-cat-slug" required>
              Identificador
            </Label>
            <Input
              id="cr2-cat-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="manutencao"
              disabled={!!editing}
            />
            <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
              Nome curto, sem espaços nem acentos. Não muda depois de criado.
            </p>
          </div>
          <div>
            <Label htmlFor="cr2-cat-descricao">Descrição</Label>
            <Input
              id="cr2-cat-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label htmlFor="cr2-cat-cor">Cor (código hex)</Label>
              <Input
                id="cr2-cat-cor"
                value={cor}
                onChange={(e) => setCor(e.target.value)}
                placeholder="#ef4444"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="cr2-cat-ordem">Ordem</Label>
              <Input
                id="cr2-cat-ordem"
                type="number"
                value={ordem}
                onChange={(e) => setOrdem(Number(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          loading={busy}
          disabled={slug.trim().length < 1 || nome.trim().length < 1}
          onClick={() => onSubmit({ slug, nome, descricao, cor, ordem })}
          data-testid="cr2-cat-save"
        >
          {editing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── RelationshipRolesPanel ─────────────────────────────────────────────

function RelationshipRolesPanel() {
  const { data } = useRelationshipRoles({ includeInactive: true });
  const { create, update, remove } = useRelationshipRoleActions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RelationshipRole | null>(null);
  const rows = useMemo(() => data ?? [], [data]);

  return (
    <Card variant="flat">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-line dark:border-line-dark">
        <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
          {rows.length} {rows.length === 1 ? 'papel' : 'papéis'}
        </p>
        <Button
          size="sm"
          data-testid="cr2-new-role"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          + Novo papel
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<ComercialIcon className="w-6 h-6" />}
          title="Nenhum papel"
          description="Adicione o primeiro pra começar."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Recebe avisos</TableHead>
              <TableHead>Pode autorizar</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="min-w-0">
                  <span className="break-words">{r.nome}</span>
                </TableCell>
                <TableCell>
                  {r.recebeNotificacoesPadrao ? (
                    <Badge variant="success" size="sm">
                      Sim
                    </Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">
                      Não
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {r.podeAutorizarAtendimento ? (
                    <Badge variant="success" size="sm">
                      Sim
                    </Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">
                      Não
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={r.ativo}
                    onChange={() => update.mutate({ id: r.id, input: { ativo: !r.ativo } })}
                    aria-label={`Ligar/desligar ${r.nome}`}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(r);
                      setOpen(true);
                    }}
                  >
                    Editar
                  </Button>
                  {r.ativo && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(r.id)}
                      className="text-red"
                    >
                      Desativar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <RelationshipRoleDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        editing={editing}
        onSubmit={(input) => {
          if (editing) {
            update.mutate(
              { id: editing.id, input },
              { onSuccess: () => setOpen(false) },
            );
          } else {
            create.mutate(input, { onSuccess: () => setOpen(false) });
          }
        }}
        busy={create.isPending || update.isPending}
      />
    </Card>
  );
}

function RelationshipRoleDialog({
  open,
  onClose,
  editing,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  editing: RelationshipRole | null;
  onSubmit: (input: {
    slug: string;
    nome: string;
    descricao: string | null;
    ordem: number;
    recebeNotificacoesPadrao: boolean;
    podeAutorizarAtendimento: boolean;
  }) => void;
  busy?: boolean;
}) {
  const [slug, setSlug] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [recebeNotif, setRecebeNotif] = useState(false);
  const [podeAutorizar, setPodeAutorizar] = useState(false);

  useMemo(() => {
    if (open) {
      setSlug(editing?.slug ?? '');
      setNome(editing?.nome ?? '');
      setDescricao(editing?.descricao ?? '');
      setOrdem(editing?.ordem ?? 0);
      setRecebeNotif(editing?.recebeNotificacoesPadrao ?? false);
      setPodeAutorizar(editing?.podeAutorizarAtendimento ?? false);
    }
  }, [open, editing]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>
          {editing ? `Editar: ${editing.nome}` : 'Novo papel de vínculo'}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cr2-role-nome" required>
              Nome
            </Label>
            <Input
              id="cr2-role-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Síndico"
            />
          </div>
          <div>
            <Label htmlFor="cr2-role-slug" required>
              Identificador
            </Label>
            <Input
              id="cr2-role-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="sindico_atual"
              disabled={!!editing}
            />
            <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
              Nome curto, sem espaços nem acentos. Não muda depois de criado.
            </p>
          </div>
          <div>
            <Label htmlFor="cr2-role-descricao">Descrição</Label>
            <Input
              id="cr2-role-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cr2-role-ordem">Ordem</Label>
            <Input
              id="cr2-role-ordem"
              type="number"
              value={ordem}
              onChange={(e) => setOrdem(Number(e.target.value) || 0)}
            />
          </div>
          <div className="flex items-center justify-between gap-3 p-3 border border-line dark:border-line-dark rounded-md">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                Já entra na lista de avisos
              </p>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Quem tem esse papel passa a receber os avisos do cliente. Dá pra
                tirar uma pessoa específica depois.
              </p>
            </div>
            <Switch
              checked={recebeNotif}
              onChange={() => setRecebeNotif(!recebeNotif)}
              aria-label="Já entra na lista de avisos"
            />
          </div>
          <div className="flex items-center justify-between gap-3 p-3 border border-line dark:border-line-dark rounded-md">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                Pode autorizar atendimento
              </p>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Marque se quem tem esse papel pode aprovar ordens de serviço e
                agendamentos.
              </p>
            </div>
            <Switch
              checked={podeAutorizar}
              onChange={() => setPodeAutorizar(!podeAutorizar)}
              aria-label="Pode autorizar atendimento"
            />
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          loading={busy}
          disabled={slug.trim().length < 1 || nome.trim().length < 1}
          onClick={() =>
            onSubmit({
              slug,
              nome,
              descricao: descricao.trim() || null,
              ordem,
              recebeNotificacoesPadrao: recebeNotif,
              podeAutorizarAtendimento: podeAutorizar,
            })
          }
        >
          {editing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── ContractTemplatesPanel ─────────────────────────────────────────────

function ContractTemplatesPanel() {
  const { data } = useContractTemplates({ includeInactive: true });
  const { update, remove } = useContractTemplateActions();
  const rows = useMemo(() => data ?? [], [data]);

  return (
    <Card variant="flat">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-line dark:border-line-dark">
        <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
          {rows.length} {rows.length === 1 ? 'modelo' : 'modelos'}
        </p>
        <p className="min-w-0 max-w-full text-xs text-ink-3 dark:text-ink-dark-3">
          Os modelos já vêm prontos com o sistema. Por enquanto dá para ligar e
          desligar cada um.
        </p>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<ComercialIcon className="w-6 h-6" />}
          title="Nenhum modelo de contrato"
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Periodicidade</TableHead>
              <TableHead className="text-center">Prazo (h)</TableHead>
              <TableHead className="text-center">Corretivos/mês</TableHead>
              <TableHead className="text-center">Plantão</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r: ContractTemplate) => (
              <TableRow key={r.id}>
                <TableCell className="min-w-0">
                  <span className="break-words">{r.nome}</span>
                </TableCell>
                <TableCell>
                  <Badge variant={r.tipo === 'recorrente' ? 'success' : 'info'} size="sm">
                    {r.tipo === 'recorrente' ? 'Recorrente' : 'Projeto'}
                  </Badge>
                </TableCell>
                <TableCell>{r.periodicidadePreventiva ?? '—'}</TableCell>
                <TableCell className="text-center font-mono">
                  {r.slaHorasPadrao ?? '—'}
                </TableCell>
                <TableCell className="text-center font-mono">
                  {r.qtdChamadosCorretivosMes ?? '—'}
                </TableCell>
                <TableCell className="text-center font-mono">
                  {r.temPlantao ? `${r.qtdPlantoesMes ?? 0}` : '—'}
                </TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={r.ativo}
                    onChange={() => update.mutate({ id: r.id, input: { ativo: !r.ativo } })}
                    aria-label={`Ligar/desligar ${r.nome}`}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  {r.ativo && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(r.id)}
                      className="text-red"
                    >
                      Desativar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
